import React from 'react';
import _capitalize from 'lodash/capitalize';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { DotSeparator, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { Link } from '@/components/Link';
import { getContentProgress } from '@/modules/content/components/ContentCard/utils/getContentProgress';
import { getOverdue } from '@/modules/content/components/ContentCard/utils/getOverdue';
import { isEventRegistered } from '@/modules/events/views/EventDetails/components/RecurringInstanceCarousal/Card';
import {
  ContentEntity,
  ContentPerformanceEntity,
  EventUnion,
  WidgetTypeEnum,
} from '@/types/schema';
import getHumanDuration from '@/utils/time/getHumanDuration';

import {
  getEntityDuration,
  getEntityId,
  getEntityImage,
  getEntityName,
  getEntityStartTime,
  getEntityUrl,
  getIsEventEntity,
} from '../../../../utils/homepageWidgetsDataAdapterUtil';
import { LayoutType } from '../../../../utils/layouts';
import { CONTENT_TYPE_WIDGETS } from '../../../HomepageGallery/components/GalleryGridCard';
import { WidgetEntityUnion } from '../../../types';
import { getAltText, getImageHeightWidth } from '../../utils';
import DueOn from '../DueOn';
import Progress from '../Progress';
import RegisteredPill from '../Registered';
import { listItemImageStyles } from './styles';

interface ListItemProps {
  entity?: WidgetEntityUnion | null;
  layout: LayoutType;
  widgetType: WidgetTypeEnum;
  containerClassName?: string;
  isCalendar?: boolean;
  noDescription?: boolean;
}

function ListItem({
  entity,
  layout,
  widgetType,
  containerClassName,
  isCalendar = false,
  noDescription = false,
}: ListItemProps) {
  const { t } = useTranslation();
  const { isDesktop, isMobile } = useScreen();
  const [imageHeight, imageWidth] = getImageHeightWidth(layout, isDesktop);

  if (!widgetType || !entity) {
    return null;
  }

  const entityId = getEntityId(entity, widgetType);
  const view = getEntityUrl(entity, widgetType);
  const entityName = getEntityName(entity, widgetType) || '';
  const entityImage = getEntityImage(entity, widgetType) || '';
  const isEventEntity = getIsEventEntity(widgetType);
  const entityDuration = getEntityDuration(entity, widgetType); // for content items
  const entityStartTime = getEntityStartTime(entity, widgetType); // for event items
  const overdue = CONTENT_TYPE_WIDGETS.includes(widgetType)
    ? getOverdue(t, entity as ContentEntity)
    : null;
  const progress = CONTENT_TYPE_WIDGETS.includes(widgetType)
    ? getContentProgress(entity as ContentEntity).progress
    : null;

  const entityType =
    widgetType === WidgetTypeEnum.Calendar
      ? (entity as ContentPerformanceEntity)?.entity?.type
      : entity.type;

  const labelVariant = isDesktop ? 'label-03' : 'body-short-03';
  const headingVariant = isDesktop ? 'heading-xs' : 'heading-xxs';

  const showRegisteredPill = isEventRegistered(entity as EventUnion);

  const height = isCalendar ? (isMobile ? 90 : 67.2) : imageHeight;

  const listItemMaxHeight = noDescription ? 'max-h-[100.86px]' : 'max-h-[97.43px]';

  return (
    <Link key={entityId} className={cn('w-full flex-1', listItemMaxHeight)} to={view}>
      <Stack
        alignItems="center"
        className={cn('w-full h-full px-18 py-8', containerClassName)}
        direction="row"
        gapX={12}>
        <Image
          alt={getAltText(entityName)}
          className={cn(
            'object-cover object-center rounded-12',
            listItemImageStyles(height / 10, imageWidth / 10), // ÷10 to convert px to rem
          )}
          height={height}
          src={entityImage}
          width={imageWidth}
        />
        <Stack className="h-full w-full" direction="col" gap={4} justifyContent="between">
          <Stack className="w-full" direction="col" gap={4}>
            <Stack alignItems="center" className="h-24" direction="row" justifyContent="between">
              <DotSeparator
                dotProps={{
                  background: 'bg-text-secondary',
                }}
                gapX={8}>
                <Typography className="text-text-secondary" variant={labelVariant}>
                  {_capitalize(entityType)}
                </Typography>
                {entityDuration || entityStartTime ? (
                  <Typography className="text-text-secondary" variant={labelVariant}>
                    {isEventEntity && entityStartTime
                      ? entityStartTime
                      : getHumanDuration(entityDuration)}
                  </Typography>
                ) : null}
              </DotSeparator>
              <Stack gap={4}>
                {isDesktop && overdue ? <DueOn overdue={overdue} /> : null}
                {isDesktop && progress ? <Progress progress={progress} /> : null}
                {isDesktop && showRegisteredPill ? (
                  <RegisteredPill showColor useSmallLabel />
                ) : null}
              </Stack>
            </Stack>
            <Typography variant={headingVariant}>{entityName}</Typography>
          </Stack>
          <Stack gap={4}>
            {!isDesktop && overdue ? <DueOn overdue={overdue} showIcon={true} /> : null}
            {!isDesktop && progress ? <Progress progress={progress} /> : null}
            {!isDesktop && showRegisteredPill ? <RegisteredPill showColor useSmallLabel /> : null}
          </Stack>
        </Stack>
      </Stack>
    </Link>
  );
}

export default ListItem;
